import React, { useState } from 'react';
import { Table, Button, Space, Typography, Tag, Form, Input, DatePicker, Select, message, Card } from 'antd';
import { SearchOutlined, DownloadOutlined } from '@ant-design/icons';
import { apiClient as api } from '../../../shared/api/axios-instance';

const { Title } = Typography;
const { Option } = Select;
const { RangePicker } = DatePicker;

export const MerchantLedgerViewer: React.FC = () => {
    const [data, setData] = useState<any[]>([]);
    const [loading, setLoading] = useState(false);
    const [pagination, setPagination] = useState({ current: 1, pageSize: 25, total: 0 });
    const [form] = Form.useForm();
    const [currentMerchantId, setCurrentMerchantId] = useState<number | null>(null);

    const fetchLedger = async (merchantId: number, params: any = {}, page = 1, pageSize = 25) => {
        try {
            setLoading(true);
            setCurrentMerchantId(merchantId);
            const queryParams = new URLSearchParams({
                page: page.toString(),
                per_page: pageSize.toString(),
                ...params
            });
            const response = await api.get(`/admin/financials/wallets/${merchantId}/ledger?${queryParams}`);
            setData(response.data.data.data);
            setPagination({
                current: response.data.data.current_page,
                pageSize: response.data.data.per_page,
                total: response.data.data.total,
            });
        } catch (error: any) {
            message.error(error.response?.data?.message || 'Failed to load ledger');
        } finally {
            setLoading(false);
        }
    };

    const handleSearch = (values: any) => {
        if (!values.merchant_id) {
            message.warning('Please enter a Merchant ID to view their ledger.');
            return;
        }

        const params: any = {};
        if (values.date_range && values.date_range.length === 2) {
            params.start_date = values.date_range[0].format('YYYY-MM-DD');
            params.end_date = values.date_range[1].format('YYYY-MM-DD');
        }
        if (values.transaction_type) params.transaction_type = values.transaction_type;
        if (values.direction) params.direction = values.direction;
        if (values.reference_id) params.reference_id = values.reference_id;

        fetchLedger(values.merchant_id, params, 1, pagination.pageSize);
    };

    const handleTableChange = (newPagination: any) => {
        const values = form.getFieldsValue();
        const params: any = {};
        if (values.date_range && values.date_range.length === 2) {
            params.start_date = values.date_range[0].format('YYYY-MM-DD');
            params.end_date = values.date_range[1].format('YYYY-MM-DD');
        }
        if (values.transaction_type) params.transaction_type = values.transaction_type;
        if (values.direction) params.direction = values.direction;
        if (values.reference_id) params.reference_id = values.reference_id;

        fetchLedger(currentMerchantId!, params, newPagination.current, newPagination.pageSize);
    };

    const exportToCsv = () => {
        const headers = ['Date', 'Ref Number', 'Transaction Type', 'Description', 'Amount', 'Balance After'];
        const csvRows = [headers.join(',')];
        data.forEach(row => {
            csvRows.push([
                row.created_at,
                row.reference_number || row.reference_id,
                row.type,
                `"${row.description || ''}"`,
                row.amount,
                row.balance_after
            ].join(','));
        });
        const blob = new Blob([csvRows.join('\n')], { type: 'text/csv' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `merchant_ledger_${currentMerchantId}_${new Date().getTime()}.csv`;
        a.click();
    };

    const columns = [
        { title: 'Date', dataIndex: 'created_at', key: 'created_at' },
        { 
            title: 'Reference', 
            key: 'reference', 
            render: (_: any, record: any) => record.reference_number || record.reference_id || 'N/A' 
        },
        { 
            title: 'Type', 
            dataIndex: 'type', 
            key: 'type',
            render: (type: string) => {
                const isCredit = type.includes('credit');
                return <Tag color={isCredit ? 'success' : 'error'}>{type}</Tag>;
            }
        },
        { title: 'Description', dataIndex: 'description', key: 'description' },
        { 
            title: 'Amount', 
            dataIndex: 'amount', 
            key: 'amount',
            render: (amount: number, record: any) => {
                const isCredit = record.type.includes('credit');
                return <span style={{ color: isCredit ? '#3f8600' : '#cf1322' }}>{isCredit ? '+' : '-'}{amount}</span>;
            }
        },
        { title: 'Balance After', dataIndex: 'balance_after', key: 'balance_after' },
    ];

    return (
        <div>
            <Title level={2}>Merchant Ledger</Title>

            <Card style={{ marginBottom: 16 }}>
                <Form form={form} layout="inline" onFinish={handleSearch}>
                    <Form.Item name="merchant_id" rules={[{ required: true, message: 'Merchant ID required' }]}>
                        <Input placeholder="Merchant ID" type="number" style={{ width: 150 }} />
                    </Form.Item>
                    <Form.Item name="date_range">
                        <RangePicker />
                    </Form.Item>
                    <Form.Item name="transaction_type">
                        <Select placeholder="Transaction Type" style={{ width: 150 }} allowClear>
                            <Option value="recharge_credit">Recharge</Option>
                            <Option value="subscription_debit">Subscription</Option>
                            <Option value="adjustment_credit">Credit Adj.</Option>
                            <Option value="adjustment_debit">Debit Adj.</Option>
                        </Select>
                    </Form.Item>
                    <Form.Item name="direction">
                        <Select placeholder="Direction" style={{ width: 100 }} allowClear>
                            <Option value="credit">Credit</Option>
                            <Option value="debit">Debit</Option>
                        </Select>
                    </Form.Item>
                    <Form.Item name="reference_id">
                        <Input placeholder="Reference ID/Number" />
                    </Form.Item>
                    <Form.Item>
                        <Button type="primary" htmlType="submit" icon={<SearchOutlined />}>
                            Search
                        </Button>
                    </Form.Item>
                </Form>
            </Card>

            <div style={{ marginBottom: 16, textAlign: 'right' }}>
                <Button 
                    icon={<DownloadOutlined />} 
                    onClick={exportToCsv} 
                    disabled={!data.length}
                >
                    Export CSV
                </Button>
            </div>

            <Table 
                columns={columns} 
                dataSource={data} 
                rowKey="id" 
                loading={loading}
                pagination={pagination}
                onChange={handleTableChange}
            />
        </div>
    );
};
